import React, { useState, useEffect } from 'react';
import '../styles/activities.css';
import ActivityContentRenderer from '../components/ActivityContentRenderer';
import { supabase } from '../integrations/client.ts';
import supabaseLocalData from '../fake_data/supabase-local.json';

export function CodeEditor() {
    return (
        <div className='code_test_container'>
            {/* Judge0 Code Editor and Output */}
        </div>
    );
}


function QuestionCounter({ currentQuestionIndex, totalQuestions, onPrevious, onNext }) {
    return (
        <div className='question_counter'>
            <div className="question-counter-header">
                <div className="question-counter-left">
                    <h2>Question {currentQuestionIndex + 1} out of {totalQuestions}</h2>
                </div>
                <div className="question-counter-right">
                    <button 
                        className='question_nav_button' 
                        onClick={onPrevious}
                        disabled={currentQuestionIndex === 0}
                    >
                        Prev
                    </button>
                    <button 
                        className='question_nav_button' 
                        onClick={onNext}
                        disabled={currentQuestionIndex === totalQuestions - 1}
                    >
                        Next
                    </button>
                </div>
            </div>
        </div>
    );
}

function Prompt({ content }) {
    return (
        <div className='prompt_container'>
            <ActivityContentRenderer content={content} />
        </div>
    );
}

function AnswerChoices({ choices, selectedAnswer, onAnswerSelect }) {
    const handleChoiceClick = (choiceIndex) => {
        onAnswerSelect(choiceIndex);
    };

    return (
        <div className='answer_choices_container'>
            <h2>Select your answer:</h2>
            <div className='choices_grid'>
                {choices.map((choice, index) => (
                    <button
                        key={index}
                        className={`choice_button ${selectedAnswer === index ? 'selected' : ''}`}
                        onClick={() => handleChoiceClick(index)}
                    >
                        <span className='choice_label'>{String.fromCharCode(65 + index)}.</span>
                        <span className='choice_text'>{choice}</span>
                    </button>
                ))}
            </div>
        </div>
    );
}

export function Quiz({ testId, sectionId, onSubmit, allowSubmit = true }) {
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [selectedAnswers, setSelectedAnswers] = useState({});
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchQuestions = async () => {
            if (!testId) {
                setQuestions([]);
                setLoading(false);
                return;
            }

            try {
                setLoading(true);

                const { data, error } = await supabase
                    .from('questions')
                    .select('*')
                    .eq('test_id', testId)
                    .order('order', { ascending: true });

                if (error) {
                    // Fallback to fake data
                    const fakeQuestions = supabaseLocalData.questions
                        .filter(q => q.test_id === testId)
                        .sort((a, b) => a.order - b.order);
                    setQuestions(fakeQuestions);
                } else {
                    setQuestions(data || []);
                }
            } catch (err) {
                setQuestions([]);
            } finally {
                setLoading(false);
            }
        };

        fetchQuestions();
    }, [testId]);

    const handlePrevious = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(currentQuestionIndex - 1);
        }
    };

    const handleNext = () => {
        if (currentQuestionIndex < questions.length - 1) {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
        }
    };

    const handleAnswerSelect = (answerIndex) => {
        setSelectedAnswers({
            ...selectedAnswers,
            [currentQuestionIndex]: answerIndex
        });
    };

    const handleSubmitQuiz = () => {
        // Convert selectedAnswers object to array format
        const answers = questions.map((question, index) => ({
            questionId: question.id,
            selectedAnswer: selectedAnswers[index] ?? -1 // -1 if not answered
        }));

        if (onSubmit) {
            onSubmit(answers);
        }
    };

    // Check if all questions are answered
    const allQuestionsAnswered = questions.length > 0 &&
        questions.every((_, index) => selectedAnswers[index] !== undefined);

    if (loading) {
        return (
            <div className='quiz_container'>
                <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                    Loading questions...
                </div>
            </div>
        );
    }

    if (questions.length === 0) {
        return (
            <div className='quiz_container'>
                <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                    No questions available for this test.
                </div>
            </div>
        );
    }

    const currentQuestion = questions[currentQuestionIndex];

    return (
        <div className='quiz_container'>
            <QuestionCounter
                currentQuestionIndex={currentQuestionIndex}
                totalQuestions={questions.length}
                onPrevious={handlePrevious}
                onNext={handleNext}
            />
            <Prompt content={currentQuestion.question} />
            <AnswerChoices
                choices={currentQuestion.options}
                selectedAnswer={selectedAnswers[currentQuestionIndex]}
                onAnswerSelect={handleAnswerSelect}
            />

            {/* Submit button */}
            {allowSubmit && (
                <div className='quiz-submit-container'>
                    <button
                        className={`quiz-submit-btn ${!allQuestionsAnswered ? 'disabled' : ''}`}
                        onClick={handleSubmitQuiz}
                        disabled={!allQuestionsAnswered}
                    >
                        {allQuestionsAnswered ? 'Submit Quiz' : `Answer All Questions (${Object.keys(selectedAnswers).length}/${questions.length})`}
                    </button>
                </div>
            )}
        </div>
    );
}